import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  type Node,
  type Edge,
  useNodesState,
  type NodeTypes,
  Handle,
  Position,
  useReactFlow,
  ReactFlowProvider,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { useAgentConversations } from "@/hooks/use-agent-conversations";
import { agentsApi, type Agent } from "@/api/agents";
import { queryKeys } from "@/lib/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AgentSidecar } from "@/components/workspace/agent-sidecar";
import { cn } from "@/lib/utils";
import { Bot, Cpu, MessageSquare, FolderPlus, ExternalLink, X, RotateCcw } from "lucide-react";

// --- Inline provider logos for org chart nodes ---

function AnthropicIcon() {
  return (
    <svg viewBox="0 0 256 176" className="h-3 w-3" fill="currentColor">
      <path d="M147.487 0L256 176h-54.264L93.224 0h54.263ZM108.513 0L0 176h54.264l108.512-176H108.513Z" />
    </svg>
  );
}

function OpenAIIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-3 w-3" fill="currentColor">
      <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.998 5.998 0 0 0-3.998 2.9 6.042 6.042 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073ZM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494ZM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646ZM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872v.024Zm16.597 3.855-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667Zm2.01-3.023-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66v.018ZM8.318 12.232l-2.026-1.168a.071.071 0 0 1-.038-.052V5.422a4.497 4.497 0 0 1 7.378-3.456l-.142.08L8.704 4.81a.795.795 0 0 0-.392.68l-.003 6.742h.009Zm1.1-2.367 2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5-.005-2.999Z" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-3 w-3">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23Z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62Z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53Z" />
    </svg>
  );
}

function AzureIcon() {
  return (
    <svg viewBox="0 0 96 96" className="h-3 w-3">
      <defs>
        <linearGradient id="az-oc-a" x1="58.97" y1="9.37" x2="26.93" y2="87.09" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#114a8b" /><stop offset="1" stopColor="#0669bc" />
        </linearGradient>
        <linearGradient id="az-oc-c" x1="50.16" y1="7.67" x2="79.94" y2="85.45" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#3ccbf4" /><stop offset="1" stopColor="#2892df" />
        </linearGradient>
      </defs>
      <path d="M33.34 6.54h26.04L33.12 89.02a4.59 4.59 0 0 1-4.33 3.06H12.06a4.58 4.58 0 0 1-4.33-6.12L32.61 9.6a4.59 4.59 0 0 1 4.33-3.06h.4Z" fill="url(#az-oc-a)" />
      <path d="M33.34 6.54a4.56 4.56 0 0 0-4.37 3.16L4.02 85.84a4.58 4.58 0 0 0 4.33 6.24h17.32a4.66 4.66 0 0 0 3.6-2.43l5.24-10.2 18.57 12.3a4.57 4.57 0 0 0 2.5.75h16.31L59.33 6.54Z" fill="url(#az-oc-c)" />
    </svg>
  );
}

function ModelProviderIcon({ model }: { model: string | null }) {
  if (!model) return null;
  const lower = model.toLowerCase();
  if (lower.startsWith("azure/") || lower.startsWith("azure_")) return <AzureIcon />;
  if (lower.includes("claude")) return <AnthropicIcon />;
  if (/gpt|^o[34]/.test(lower)) return <OpenAIIcon />;
  if (lower.includes("gemini")) return <GoogleIcon />;
  return <Cpu size={10} className="text-text-muted" />;
}

function getModelShortName(model: string): string {
  return model
    .replace("azure/", "")
    .replace(/^claude-/, "")
    .replace(/^gpt-/, "GPT ")
    .replace(/^gemini-/, "Gem ");
}

// --- Visual identity system ---

function hashString(s: string): number {
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) - hash + s.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

// Deterministic fallback colours for departments/roles not in the fixed
// palettes. MUST be hex — lane boxes and badges build translucent variants by
// appending an 8-digit-hex alpha suffix (e.g. `${color}40`), which is invalid
// on an `hsl(...)` string and would render the department box transparent.
const FALLBACK_HEX = [
  "#0ea5e9", "#d946ef", "#84cc16", "#ef4444", "#eab308",
  "#8b5cf6", "#14b8a6", "#f97316", "#ec4899", "#6366f1",
];

function hashColor(s: string): string {
  return FALLBACK_HEX[hashString(s) % FALLBACK_HEX.length];
}

// Per-role accent colours (hex) — drive the node's left border + avatar
// gradient. Mirrors the department palette so the chart reads cohesively.
const ROLE_ACCENT_HEX: Record<string, string> = {
  executive: "#6366f1",
  engineering: "#10b981",
  legal: "#eab308",
  finance: "#06b6d4",
  operations: "#f97316",
  marketing: "#ec4899",
  support: "#14b8a6",
  research: "#8b5cf6",
  sales: "#f43f5e",
};

function getRoleAccent(role: string): string {
  const lower = role.toLowerCase();
  for (const key of Object.keys(ROLE_ACCENT_HEX)) {
    if (lower.includes(key)) return ROLE_ACCENT_HEX[key];
  }
  if (/\b(ceo|cto|cfo|coo|chief|director|vp|president)\b/i.test(lower)) return ROLE_ACCENT_HEX.executive;
  if (/\b(dev|code|coding|engineer|software|programmer)\b/i.test(lower)) return ROLE_ACCENT_HEX.engineering;
  if (/\b(law|compliance|regulatory|counsel)\b/i.test(lower)) return ROLE_ACCENT_HEX.legal;
  if (/\b(account|budget|tax|audit)\b/i.test(lower)) return ROLE_ACCENT_HEX.finance;
  return hashColor(role);
}

const STATUS_VIS: Record<string, { color: string; label: string; pulse: boolean }> = {
  active: { color: "#10b981", label: "Active", pulse: true },
  running: { color: "#10b981", label: "Working", pulse: true },
  idle: { color: "#9ca3af", label: "Idle", pulse: false },
  paused: { color: "#f59e0b", label: "Paused", pulse: false },
  error: { color: "#ef4444", label: "Error", pulse: false },
};

function getInitials(name: string): string {
  return name
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

const DEPT_COLORS: Record<string, string> = {
  "Executive Leadership": "#6366f1",
  "IT / Technology": "#10b981",
  "Finance": "#06b6d4",
  "Human Resources": "#f59e0b",
  "Marketing": "#ec4899",
  "Sales": "#f43f5e",
  "Operations": "#f97316",
  "Legal / Compliance": "#eab308",
  "Customer Service": "#14b8a6",
  "Product / Engineering": "#8b5cf6",
};

function getDeptColor(dept: string): string {
  return DEPT_COLORS[dept] ?? hashColor(dept);
}

const UNASSIGNED = "Unassigned";

// --- Agent node ---

interface AgentNodeData {
  label: string;
  role: string;
  status: string;
  title: string | null;
  avatarUrl: string | null;
  model: string | null;
  isSubagent: boolean;
  agentId: string;
  dim?: boolean;
  highlight?: boolean;
  [key: string]: unknown;
}

function AgentNode({ data }: { data: AgentNodeData }) {
  const accent = getRoleAccent(data.role);
  const status = STATUS_VIS[data.status] ?? STATUS_VIS.idle;
  const initials = getInitials(data.label);

  return (
    <div
      className={cn(
        "flex h-full w-full flex-col gap-1.5 rounded-xl border border-border bg-surface px-3 py-2.5 shadow-sm transition-[opacity,box-shadow,transform] duration-150",
        data.dim && "opacity-40",
      )}
      style={{
        borderLeft: `3px solid ${accent}`,
        boxShadow: data.highlight
          ? `0 0 0 2px ${accent}55, 0 8px 20px rgba(0,0,0,0.10)`
          : undefined,
      }}
    >
      <Handle type="target" position={Position.Top} className="!h-2 !w-2 !border-2 !border-surface !bg-border" />

      <div className="flex items-center gap-2.5">
        <div className="relative shrink-0">
          {data.avatarUrl ? (
            <img src={data.avatarUrl} alt={data.label} className="h-8 w-8 rounded-lg object-cover" />
          ) : (
            <div
              className="flex h-8 w-8 items-center justify-center rounded-lg text-xs font-semibold text-white"
              style={{ background: `linear-gradient(135deg, ${accent}, ${accent}bb)` }}
            >
              {initials}
            </div>
          )}
          <span
            className={cn(
              "absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-surface",
              status.pulse && "status-pulse",
            )}
            style={{ background: status.color }}
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[13px] font-semibold leading-tight text-text-primary" title={data.label}>
              {data.label}
            </span>
            {data.isSubagent && (
              <span
                className="shrink-0 rounded px-1 py-px text-[8px] font-semibold"
                style={{ color: accent, background: `${accent}1a` }}
              >
                SUB
              </span>
            )}
          </div>
          <div className="truncate text-[11px] text-text-secondary" title={data.title || data.role}>
            {data.title || data.role}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="flex items-center gap-1 text-[10px] font-medium" style={{ color: status.color }}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: status.color }} />
          {status.label}
        </span>
        {data.model && (
          <span className="ml-auto flex items-center gap-1 truncate font-mono text-[10px] text-text-muted">
            <ModelProviderIcon model={data.model} />
            <span className="truncate">{getModelShortName(data.model)}</span>
          </span>
        )}
      </div>

      <Handle type="source" position={Position.Bottom} className="!h-2 !w-2 !border-2 !border-surface !bg-border" />
    </div>
  );
}

// --- Department lane node (background container) ---

function DepartmentNode({ data }: { data: { label: string; color: string; count: number } }) {
  return (
    <div
      className="h-full w-full rounded-2xl"
      style={{ border: `1.5px solid ${data.color}40`, background: `${data.color}0d` }}
    >
      <div className="flex items-center gap-2 px-3.5 py-3">
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: data.color }} />
        <span className="text-[13px] font-semibold text-text-primary">{data.label}</span>
        <span
          className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
          style={{ color: data.color, background: `${data.color}1a` }}
        >
          {data.count}
        </span>
      </div>
    </div>
  );
}

const nodeTypes: NodeTypes = {
  agent: AgentNode as unknown as NodeTypes["agent"],
  department: DepartmentNode as unknown as NodeTypes["department"],
};

// --- Lane-packing layout ---
//
// Agents are grouped by department and packed into a tidy grid inside their
// department's lane. A new agent always lands in the next free cell of its
// department, so it can never end up orphaned or misaligned. Lanes flow
// left-to-right, wrapping every LANES_PER_ROW. Agent nodes do NOT use
// ReactFlow parentId, so they drag freely while reporting/live edges
// re-route automatically.

const NODE_W = 200;
const NODE_H = 96;
const GAP = 18;
const LANE_PAD = 16;
const LANE_HEAD = 48;
const LANE_GAP = 28;
const LANES_PER_ROW = 3;

function packLane(agents: Agent[]) {
  const cols = Math.min(3, Math.max(1, Math.ceil(Math.sqrt(agents.length))));
  const rows = Math.ceil(agents.length / cols) || 1;
  const w = Math.max(LANE_PAD * 2 + cols * NODE_W + (cols - 1) * GAP, 260);
  const h = Math.max(LANE_HEAD + LANE_PAD + rows * NODE_H + (rows - 1) * GAP, LANE_HEAD + LANE_PAD + NODE_H);
  const slots = agents.map((a, i) => ({
    id: a.id,
    x: LANE_PAD + (i % cols) * (NODE_W + GAP),
    y: LANE_HEAD + Math.floor(i / cols) * (NODE_H + GAP),
  }));
  return { w, h, slots };
}

function buildLaneLayout(agents: Agent[]) {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const idSet = new Set(agents.map((a) => a.id));

  // CEO first, then by name — keeps the master agent and managers near the
  // top-left of their lane.
  const order = (a: Agent, b: Agent) => {
    const aCeo = a.role === "ceo";
    const bCeo = b.role === "ceo";
    if (aCeo && !bCeo) return -1;
    if (!aCeo && bCeo) return 1;
    return a.name.localeCompare(b.name);
  };

  // Group by department.
  const byDept = new Map<string, Agent[]>();
  for (const a of agents) {
    const dept = a.department || UNASSIGNED;
    if (!byDept.has(dept)) byDept.set(dept, []);
    byDept.get(dept)!.push(a);
  }

  // Department order: CEO's department first, then alphabetical, Unassigned last.
  const ceoDept = agents.find((a) => a.role === "ceo")?.department ?? null;
  const depts = Array.from(byDept.keys()).sort((a, b) => {
    if (a === UNASSIGNED) return 1;
    if (b === UNASSIGNED) return -1;
    if (a === ceoDept) return -1;
    if (b === ceoDept) return 1;
    return a.localeCompare(b);
  });

  // Place lanes left-to-right, wrapping every LANES_PER_ROW, tracking row height.
  let x = 0;
  let y = 0;
  let rowH = 0;
  let col = 0;

  for (const dept of depts) {
    const deptAgents = (byDept.get(dept) ?? []).slice().sort(order);
    const { w, h, slots } = packLane(deptAgents);

    if (col >= LANES_PER_ROW) {
      col = 0;
      x = 0;
      y += rowH + LANE_GAP;
      rowH = 0;
    }

    const laneX = x;
    const laneY = y;
    const color = dept === UNASSIGNED ? "#9ca3af" : getDeptColor(dept);

    nodes.push({
      id: `dept-${dept}`,
      type: "department",
      position: { x: laneX, y: laneY },
      data: { label: dept, color, count: deptAgents.length },
      style: { width: w, height: h, zIndex: 0 },
      draggable: false,
      selectable: false,
      deletable: false,
    });

    const slotById = new Map(slots.map((s) => [s.id, s]));
    for (const agent of deptAgents) {
      const slot = slotById.get(agent.id)!;
      nodes.push({
        id: agent.id,
        type: "agent",
        position: { x: laneX + slot.x, y: laneY + slot.y },
        data: {
          label: agent.name,
          role: agent.role,
          status: agent.status,
          title: agent.title,
          avatarUrl: agent.avatarUrl ?? null,
          model: (agent.adapterConfig?.model as string) ?? null,
          isSubagent: !!agent.parentAgentId,
          agentId: agent.id,
        } satisfies AgentNodeData,
        style: { width: NODE_W, height: NODE_H, zIndex: 10 },
      });
    }

    x += w + LANE_GAP;
    rowH = Math.max(rowH, h);
    col++;
  }

  // Reporting edges — solid grey, only when both ends exist.
  for (const a of agents) {
    if (a.reportsTo && idSet.has(a.reportsTo)) {
      edges.push({
        id: `rep-${a.reportsTo}-${a.id}`,
        source: a.reportsTo,
        target: a.id,
        type: "smoothstep",
        style: { stroke: "#cbd5e1", strokeWidth: 1.5 },
        zIndex: 1,
        data: { kind: "report" },
      });
    }
  }

  return { initialNodes: nodes, initialEdges: edges };
}

// The lane layout is authoritative: agents always render inside their
// department lane (and a reassigned/new agent immediately re-packs into the
// right one). Dragging is still allowed for in-session nudging, but positions
// aren't persisted — otherwise a saved spot would pull an agent out of its
// department box. Clear any layout saved by the previous (free-drag) version.
function clearLegacyLayout(tenantId: string) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(`proclaw_org_layout_${tenantId}`);
  } catch {
    // ignore
  }
}

// --- Context menu ---

interface ContextMenuState {
  x: number;
  y: number;
  agentId: string;
  agentName: string;
  department: string | null;
}

function ContextMenu({
  menu,
  onClose,
  onChatToAgent,
  onReassignDepartment,
  onViewAgent,
}: {
  menu: ContextMenuState;
  onClose: () => void;
  onChatToAgent: (agentId: string) => void;
  onReassignDepartment: (agentId: string) => void;
  onViewAgent: (agentId: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as HTMLElement)) onClose();
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  const items = [
    { label: "Chat to agent", icon: MessageSquare, action: () => onChatToAgent(menu.agentId) },
    { label: "View details", icon: ExternalLink, action: () => onViewAgent(menu.agentId) },
    { label: "Reassign department", icon: FolderPlus, action: () => onReassignDepartment(menu.agentId) },
  ];

  return (
    <div
      ref={ref}
      className="fixed z-50 min-w-[196px] rounded-xl border border-border bg-surface py-1 shadow-xl animate-in fade-in zoom-in-95 duration-100"
      style={{ left: menu.x, top: menu.y }}
    >
      <div className="border-b border-border px-3 py-1.5">
        <div className="text-[13px] font-semibold text-text-primary">{menu.agentName}</div>
        <div className="text-[11px] text-text-muted">{menu.department ?? "Unassigned"}</div>
      </div>
      {items.map((item) => (
        <button
          key={item.label}
          onClick={() => { item.action(); onClose(); }}
          className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-text-primary transition-colors hover:bg-surface-hover"
        >
          <item.icon size={14} className="text-text-secondary" />
          {item.label}
        </button>
      ))}
    </div>
  );
}

// --- Department assignment dialog ---

function DepartmentDialog({
  agentId,
  agentName,
  currentDepartment,
  existingDepartments,
  onClose,
  onSave,
  isSaving,
}: {
  agentId: string;
  agentName: string;
  currentDepartment: string | null;
  existingDepartments: string[];
  onClose: () => void;
  onSave: (agentId: string, department: string | null) => void;
  isSaving: boolean;
}) {
  const [selected, setSelected] = useState(currentDepartment ?? "");
  const [custom, setCustom] = useState("");

  const effectiveDept = custom.trim() || selected || null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-xl border border-border bg-surface shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-text-primary">Reassign Department</h3>
            <p className="mt-0.5 text-sm text-text-secondary">{agentName}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 transition-colors hover:bg-surface-hover">
            <X size={16} className="text-text-muted" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {existingDepartments.length > 0 && (
            <div>
              <label className="mb-2 block text-xs font-medium text-text-secondary">Existing Departments</label>
              <div className="flex flex-wrap gap-2">
                {existingDepartments.map((dept) => (
                  <button
                    key={dept}
                    onClick={() => { setSelected(dept); setCustom(""); }}
                    className={cn(
                      "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                      selected === dept && !custom
                        ? "border-accent bg-accent/10 text-accent"
                        : "border-border text-text-secondary hover:bg-surface-hover",
                    )}
                  >
                    {dept}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="mb-1.5 block text-xs font-medium text-text-secondary">Or create a new department</label>
            <input
              type="text"
              value={custom}
              onChange={(e) => { setCustom(e.target.value); setSelected(""); }}
              placeholder="e.g. Research & Development"
              className="w-full rounded-lg border border-border bg-surface-inset px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
            />
          </div>

          {currentDepartment && (
            <button
              onClick={() => { setSelected(""); setCustom(""); onSave(agentId, null); }}
              className="text-xs text-red-500 transition-colors hover:text-red-400"
            >
              Remove from current department ({currentDepartment})
            </button>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-text-secondary transition-colors hover:bg-surface-hover">
            Cancel
          </button>
          <button
            onClick={() => onSave(agentId, effectiveDept)}
            disabled={isSaving || !effectiveDept}
            className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent/90 disabled:opacity-50"
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Live conversation windows ---
const LIVE_WINDOW_MS = 15 * 60 * 1000; // animated "live" links
const SHOW_WINDOW_MS = 24 * 60 * 60 * 1000; // faint "recent" links

// --- Main component ---

export function OrgChart() {
  // ReactFlowProvider so the inner component can call useReactFlow() for the
  // auto-arrange / fit-view button.
  return (
    <ReactFlowProvider>
      <OrgChartInner />
    </ReactFlowProvider>
  );
}

function OrgChartInner() {
  const { tenantId } = useTenant();
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const { data: conversations = [] } = useAgentConversations(tenantId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const reactFlow = useReactFlow();

  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [deptDialog, setDeptDialog] = useState<{ agentId: string; agentName: string; currentDepartment: string | null } | null>(null);
  const [sidecarAgentId, setSidecarAgentId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [showComms, setShowComms] = useState(true);
  const [showReports, setShowReports] = useState(true);

  const updateDepartment = useMutation({
    mutationFn: ({ agentId, department }: { agentId: string; department: string | null }) =>
      agentsApi.update(agentId, { department }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.byCompany(tenantId) });
      setDeptDialog(null);
    },
  });

  const existingDepartments = useMemo(() => {
    const set = new Set<string>();
    for (const a of agents) if (a.department) set.add(a.department);
    return Array.from(set).sort();
  }, [agents]);

  const { initialNodes, initialEdges } = useMemo(() => buildLaneLayout(agents), [agents]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);

  // Drop any layout persisted by the previous free-drag version so it can't
  // pull agents out of their department lanes.
  useEffect(() => {
    clearLegacyLayout(tenantId);
  }, [tenantId]);

  // Re-pack whenever the agents data changes (add / remove / reassign) so a
  // reassigned or new agent immediately lands inside its department lane.
  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes, setNodes]);

  const handleResetLayout = useCallback(() => {
    setNodes(initialNodes);
    setTimeout(() => reactFlow.fitView({ padding: 0.25, duration: 400 }), 50);
  }, [initialNodes, setNodes, reactFlow]);

  // Live / recent conversation links between agents. Collapse each unordered
  // pair to its most recent activity, then bucket by age.
  const commsEdges = useMemo<Edge[]>(() => {
    const valid = new Set(agents.map((a) => a.id));
    const pairMax = new Map<string, { a: string; b: string; last: number }>();
    for (const c of conversations) {
      if (c.fromAgentId === c.toAgentId) continue;
      if (!valid.has(c.fromAgentId) || !valid.has(c.toAgentId)) continue;
      const [a, b] = [c.fromAgentId, c.toAgentId].sort();
      const ts = c.lastMessageAt ?? c.createdAt;
      const t = ts ? Date.parse(ts) : NaN;
      if (Number.isNaN(t)) continue;
      const cur = pairMax.get(`${a}|${b}`);
      if (!cur || t > cur.last) pairMax.set(`${a}|${b}`, { a, b, last: t });
    }
    const now = Date.now();
    const out: Edge[] = [];
    for (const { a, b, last } of pairMax.values()) {
      const age = now - last;
      if (age > SHOW_WINDOW_MS) continue;
      const live = age <= LIVE_WINDOW_MS;
      out.push({
        id: `comms-${a}-${b}`,
        source: a,
        target: b,
        type: "default",
        animated: live,
        selectable: false,
        zIndex: 1,
        style: {
          stroke: "#10b981",
          strokeWidth: live ? 2.5 : 1.5,
          strokeDasharray: "6 6",
          opacity: live ? 0.9 : 0.4,
        },
        data: { kind: "comms", live },
      });
    }
    return out;
  }, [conversations, agents]);

  const liveCount = useMemo(
    () => commsEdges.filter((e) => (e.data as { live?: boolean } | undefined)?.live).length,
    [commsEdges],
  );

  // Hover-to-trace: hovered agent + its manager + reports + conversation partners.
  const relSet = useMemo(() => {
    if (!hoveredId) return null;
    const s = new Set<string>([hoveredId]);
    for (const a of agents) {
      if (a.id === hoveredId && a.reportsTo) s.add(a.reportsTo);
      if (a.reportsTo === hoveredId) s.add(a.id);
    }
    for (const e of commsEdges) {
      if (e.source === hoveredId) s.add(e.target);
      if (e.target === hoveredId) s.add(e.source);
    }
    return s;
  }, [hoveredId, agents, commsEdges]);

  const displayNodes = useMemo(
    () =>
      nodes.map((n) => {
        if (n.type !== "agent") return n;
        const dim = relSet ? !relSet.has(n.id) : false;
        const highlight = hoveredId === n.id;
        if (!dim && !highlight && !n.data.dim && !n.data.highlight) return n;
        return { ...n, data: { ...n.data, dim, highlight } };
      }),
    [nodes, relSet, hoveredId],
  );

  const displayEdges = useMemo(() => {
    const all = [
      ...(showReports ? initialEdges : []),
      ...(showComms ? commsEdges : []),
    ];
    if (!relSet) return all;
    return all.map((e) => {
      const on = relSet.has(e.source) && relSet.has(e.target);
      return { ...e, style: { ...e.style, opacity: on ? (e.style?.opacity ?? 1) : 0.1 } };
    });
  }, [initialEdges, commsEdges, showReports, showComms, relSet]);

  const onNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (node.type !== "agent") return;
      event.preventDefault();
      const agent = agents.find((a) => a.id === node.id);
      if (!agent) return;
      setContextMenu({
        x: event.clientX,
        y: event.clientY,
        agentId: node.id,
        agentName: agent.name,
        department: agent.department,
      });
    },
    [agents],
  );

  const handleChatToAgent = useCallback(
    (agentId: string) => navigate(`/tenants/${tenantId}/workspace?agent=${agentId}`),
    [navigate, tenantId],
  );
  const handleViewAgent = useCallback(
    (agentId: string) => navigate(`/tenants/${tenantId}/agents/${agentId}`),
    [navigate, tenantId],
  );
  const handleReassignDepartment = useCallback(
    (agentId: string) => {
      const agent = agents.find((a) => a.id === agentId);
      if (!agent) return;
      setDeptDialog({ agentId, agentName: agent.name, currentDepartment: agent.department });
    },
    [agents],
  );
  const handleSaveDepartment = useCallback(
    (agentId: string, department: string | null) => updateDepartment.mutate({ agentId, department }),
    [updateDepartment],
  );

  const deptCount = useMemo(() => {
    const set = new Set<string>();
    for (const a of agents) set.add(a.department || UNASSIGNED);
    return set.size;
  }, [agents]);

  if (agents.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-center">
        <Bot size={40} className="mb-3 text-text-muted" />
        <p className="text-sm text-text-secondary">No agents to display. Create agents first.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col">
      {/* Toolbar */}
      <div className="flex min-h-14 shrink-0 flex-wrap items-center gap-x-3.5 gap-y-2 border-b border-border bg-surface px-6 py-2 lg:px-10">
        <div className="flex min-w-0 flex-1 items-baseline gap-2">
          <h1 className="shrink-0 text-2xl font-semibold text-text-primary">
            Organisation
          </h1>
          <span className="min-w-0 truncate text-[12.5px] text-text-muted">
            {agents.length} agent{agents.length !== 1 ? "s" : ""} · {deptCount} department{deptCount !== 1 ? "s" : ""}
          </span>
        </div>

        <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2.5">
          <button
            onClick={() => setShowComms((v) => !v)}
            className={cn("flex shrink-0 items-center gap-1.5 text-[12.5px] transition-opacity", !showComms && "opacity-40")}
            title="Toggle live conversation links"
          >
            <svg width="26" height="8">
              <line x1="0" y1="4" x2="26" y2="4" stroke="#10b981" strokeWidth="2.5" strokeDasharray="6 6" strokeLinecap="round" />
            </svg>
            <span className="text-text-secondary">{liveCount} live</span>
          </button>
          <button
            onClick={() => setShowReports((v) => !v)}
            className={cn("flex shrink-0 items-center gap-1.5 text-[12.5px] transition-opacity", !showReports && "opacity-40")}
            title="Toggle reporting lines"
          >
            <svg width="26" height="8">
              <line x1="0" y1="4" x2="26" y2="4" stroke="#cbd5e1" strokeWidth="1.8" />
            </svg>
            <span className="text-text-secondary">reporting</span>
          </button>
          <button
            onClick={handleResetLayout}
            className="flex shrink-0 items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-[13px] font-medium text-text-primary transition-colors hover:bg-surface-hover"
            title="Re-pack agents into department lanes"
          >
            <RotateCcw size={14} />
            Auto-arrange
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div className="relative min-h-0 flex-1">
        <ReactFlow
          nodes={displayNodes}
          edges={displayEdges}
          onNodesChange={onNodesChange}
          nodeTypes={nodeTypes}
          onNodeContextMenu={onNodeContextMenu}
          onNodeMouseEnter={(_, node) => { if (node.type === "agent") setHoveredId(node.id); }}
          onNodeMouseLeave={() => setHoveredId(null)}
          onNodeDoubleClick={(_, node) => {
            const id = (node.data as { agentId?: string } | undefined)?.agentId;
            if (id) setSidecarAgentId(id);
          }}
          onPaneClick={() => setContextMenu(null)}
          fitView
          fitViewOptions={{ padding: 0.25 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background />
          <Controls />
        </ReactFlow>

        {/* Hint bar */}
        <div className="pointer-events-none absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-3 rounded-[10px] bg-[rgba(31,30,27,0.92)] px-4 py-2 text-[12.5px] text-white backdrop-blur-sm">
          <span><b className="font-semibold">Drag</b> to reposition</span>
          <span className="opacity-40">·</span>
          <span><b className="font-semibold">Right-click</b> for actions</span>
          <span className="opacity-40">·</span>
          <span><b className="font-semibold">Hover</b> to trace conversations</span>
        </div>

        {contextMenu && (
          <ContextMenu
            menu={contextMenu}
            onClose={() => setContextMenu(null)}
            onChatToAgent={handleChatToAgent}
            onReassignDepartment={handleReassignDepartment}
            onViewAgent={handleViewAgent}
          />
        )}

        {deptDialog && (
          <DepartmentDialog
            agentId={deptDialog.agentId}
            agentName={deptDialog.agentName}
            currentDepartment={deptDialog.currentDepartment}
            existingDepartments={existingDepartments}
            onClose={() => setDeptDialog(null)}
            onSave={handleSaveDepartment}
            isSaving={updateDepartment.isPending}
          />
        )}

        {sidecarAgentId && (
          <AgentSidecar agentId={sidecarAgentId} onClose={() => setSidecarAgentId(null)} />
        )}
      </div>
    </div>
  );
}
